import {Indicator} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import Image from 'next/image';
import {useRouter} from 'next/router';
import {type FC, useEffect, useState} from 'react';
import {MdMenu, MdNotifications} from 'react-icons/md';
import {routes} from '../../../../config/constants';
import {
  LOGO_IMAGE_PATH_FULL,
  LOGO_IMAGE_PATH_SQUARE,
} from '../../../../config/constants/img';
import {
  isReleasedCareerAdviser,
  isReleasedRedesignTop,
} from '../../../../config/unReleasePage';
import {useNotificationsCounts} from '../../../../core/repositories/notificationsRepository';
import {useAuth} from '../../../../core/usecases/auth/AuthContext';
import {INTERVIEW_TRAINING_FEATURE_NAME} from '../../../../features/careerAdviser/constants/metaData';
import {ENTRY_MANAGEMENT_FEATURE_NAME} from '../../../../features/entryManagements/constants/metaData';
import {useEntryManagementReminders} from '../../../../features/entryManagements/usecase/useEntryManagementReminders';
import {cn} from '../../../../lib/classname';
import {useBooleanState} from '../../../services/useBooleanState';
import {useMediaQuery} from '../../../services/useMediaQuery';
import {CommonLink} from '../../CommonLink';
import {MenuItem} from './MenuItem';
import {SPDrawer} from './SPDrawer';
import {SearchBar} from './SearchBar';
import {UserMenu} from './UserMenu';

type Props = {
  className?: string;
  scoutsCount: number;
  interestsCount: number;
  todayDeadlineRecruitmentCount: number;
};

type MenuItemType = {
  name: string;
  url: string;
  target?: string;
  isNew?: boolean;
  isUpdate?: boolean;
  isEngineer?: boolean;
};

const getMenuItems = (isEngineer: boolean): MenuItemType[] => {
  const menuItems: MenuItemType[] = [
    {name: 'ホーム', url: routes.HOME},
    {name: '募集を探す', url: routes.RECRUITINGS},
    {name: '企業を探す', url: routes.COMPANIES},
    {name: 'ES・体験記', url: routes.REPORTS},
    {name: 'AI選考対策', url: routes.REPORT_ANALYTICS_SEARCH},
    ...(isReleasedCareerAdviser()
      ? [
          {
            name: INTERVIEW_TRAINING_FEATURE_NAME,
            url: routes.CAREER_ADVISER_INTERVIEW_TRAINING,
            isNew: true,
          },
        ]
      : []),
    {
      name: ENTRY_MANAGEMENT_FEATURE_NAME,
      url: routes.ENTRY_MANAGEMENTS_PROGRESS,
      isNew: true,
    },
    {name: 'コラム', url: routes.COLUMNS},
    {name: 'エンジニア', url: routes.ENGINEER, isEngineer: isEngineer},
    {name: 'スカウト', url: routes.SCOUTS},
  ];

  return menuItems;
};

export const HeaderBar: FC<Props> = ({
  className,
  scoutsCount,
  interestsCount,
  todayDeadlineRecruitmentCount,
}) => {
  const {status, signOut, user} = useAuth();
  const {isPC} = useMediaQuery();
  const [shownUserMenu, showUserMenu, hideUserMenu] = useBooleanState(false);

  // 通知数を取得（認証時のみ）
  const {counts} = useNotificationsCounts({
    config: {
      enabled: status === 'authenticated',
    },
  });
  const hasNotification = counts.unreadCount > 0;

  // リマインダー情報を取得
  const {totalCount: entryManagementRemindersCount} =
    useEntryManagementReminders();

  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [shownSearch, showSearch, hideSearch] = useBooleanState(false);
  const [shownReportMenu, showReportMenu, hideReportMenu] =
    useBooleanState(false);
  const [openedSPMenu, {open, close}] = useDisclosure(false);

  const menuItems = getMenuItems(user?.isEngineer || false);

  const isActivePath = (currentPath: string, menuUrl: string): boolean => {
    // ホームページは完全一致
    if (menuUrl === routes.HOME) {
      return currentPath === '/' || currentPath === routes.MYPAGE;
    }

    // 外部リンクは除外
    if (menuUrl.startsWith('http')) {
      return false;
    }

    // その他は前方一致
    return currentPath.startsWith(menuUrl);
  };

  useEffect(() => {
    const queryParam: string =
      typeof router.query.q === 'string' ? router.query.q : '';
    setSearchQuery(queryParam);
  }, [router.query.q]);

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const encodedSearchQuery = encodeURIComponent(searchQuery);
    void router.push(`/search?q=${encodedSearchQuery}`);
  };

  const handleMouseEnter = (item: {
    name: string;
    url: string;
    target?: string;
  }) => {
    if (item.url === routes.REPORTS) showReportMenu();
  };

  const handleMouseLeave = (item: {
    name: string;
    url: string;
    target?: string;
  }) => {
    if (item.url === routes.REPORTS) hideReportMenu();
  };

  return (
    <div className="flex pc:px-12 pc:pb-8 pc:pt-24 bg-background-1 flex-col justify-center items-center">
      <div
        className={cn(
          'flex w-full pc:max-w-[1200px] h-[68px] bg-white text-black-1  justify-center px-12 pc:px-24 pc:rounded-[24px] pc:shadow-header shadow-nav py-16',
          className,
        )}
      >
        <div className="flex w-[1200px] self-stretch justify-between items-center text-sm">
          <div className="flex items-center pc:hidden">
            <Indicator
              color="red"
              inline
              withBorder
              size={8}
              offset={2}
              disabled={
                !(
                  todayDeadlineRecruitmentCount > 0 ||
                  scoutsCount > 0 ||
                  interestsCount > 0
                )
              }
            >
              <button
                type="button"
                onClick={open}
                className="pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out"
                aria-label="ドロワーメニューを開く"
              >
                <MdMenu size={24} />
              </button>
            </Indicator>
            <SPDrawer
              opened={openedSPMenu}
              onClose={close}
              scoutsCount={scoutsCount}
              interestsCount={interestsCount}
              todayDeadlineRecruitmentCount={todayDeadlineRecruitmentCount}
            />
          </div>
          <CommonLink href={routes.HOME} className="flex items-center gap-4">
            <Image
              src={isPC ? LOGO_IMAGE_PATH_FULL : LOGO_IMAGE_PATH_SQUARE}
              alt="GSSKTロゴ"
              width={isPC ? 80 : 24}
              height={24}
              className="pc:cursor-pointer"
              priority
            />
          </CommonLink>
          <div className="hidden pc:flex justify-between items-center gap-20">
            {menuItems.map((item) => (
              <MenuItem
                key={item.name}
                item={item}
                user={user}
                handleMouseEnter={handleMouseEnter}
                handleMouseLeave={handleMouseLeave}
                shownReportMenu={shownReportMenu}
                isActive={isActivePath(router.asPath, item.url)}
                scoutsCount={scoutsCount}
                entryManagementRemindersCount={entryManagementRemindersCount}
                todayDeadlineRecruitmentCount={todayDeadlineRecruitmentCount}
              />
            ))}
          </div>
          <div className="flex gap-8 items-center">
            {!shownSearch && (
              <UserMenu
                shownUserMenu={shownUserMenu}
                showUserMenu={showUserMenu}
                hideUserMenu={hideUserMenu}
                scoutsCount={scoutsCount}
                interestsCount={interestsCount}
                isSignedIn={status === 'authenticated'}
                hasNotification={
                  isReleasedRedesignTop()
                    ? interestsCount > 0
                    : scoutsCount > 0 || interestsCount > 0
                }
                user={user}
                signOut={signOut}
              />
            )}
            {status === 'authenticated' && (
              <Indicator
                disabled={!hasNotification}
                inline
                color="red"
                offset={4}
                withBorder
              >
                <CommonLink href={routes.NOTIFICATIONS}>
                  <MdNotifications size={24} />
                </CommonLink>
              </Indicator>
            )}

            {isPC && (
              <SearchBar
                shownSearch={shownSearch}
                showSearch={showSearch}
                hideSearch={hideSearch}
                handleSearchSubmit={handleSearchSubmit}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
              />
            )}
          </div>
        </div>
      </div>
      {user?.isDemoUser && (
        <div className="bg-accent-2 p-4 w-full">
          <p className="text-base text-black text-center">
            現在プレビュー閲覧用のアカウントでログインしています。プレビュー閲覧以外を行いたい場合は個人のアカウントでログインし直してください。
          </p>
        </div>
      )}
    </div>
  );
};
